import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react"

import type { LeaveGuard } from "./application-leave-guard"
import { EditScopeProvider } from "./edit-scope"

export interface DocumentInstance<TDescriptor = unknown> {
  readonly id: string
  readonly active: boolean
  readonly descriptor: TDescriptor | null
}

export interface DocumentInstanceOperations {
  activate(): void
  close(): Promise<boolean>
}

export interface DocumentInstanceServices extends DocumentInstanceOperations {
  registerLeaveGuard(guard: LeaveGuard, options?: { readonly enableBeforeUnload?: boolean }): () => void
}

interface DocumentInstanceLifetime {
  isActive(): boolean
  retain(owner: object, dispose: () => void): void
  release(owner: object): void
  /**
   * Disconnects a resource because its React effect was cleaned up.
   * Returns false when the document is only inactive, in which case the
   * document retains the resource until the effect reconnects or the document closes.
   */
  releaseFromEffect(owner: object): boolean
}

interface DocumentInstanceContextValue extends DocumentInstance {
  readonly lifetime: DocumentInstanceLifetime
  readonly services: DocumentInstanceServices | null
}

const DocumentContext = createContext<DocumentInstanceContextValue | null>(null)

/** Hosts retain the instance until close; visibility does not destroy business state. */
export function DocumentInstanceProvider({
  id,
  active,
  descriptor = null,
  services = null,
  children,
}: Omit<DocumentInstance, "descriptor"> & {
  readonly descriptor?: unknown
  readonly services?: DocumentInstanceServices | null
  readonly children: ReactNode
}) {
  const activeRef = useRef(active)
  activeRef.current = active
  const servicesRef = useRef(services)
  servicesRef.current = services
  const stableServices = useMemo<DocumentInstanceServices | null>(
    () =>
      services == null
        ? null
        : {
            activate: () => servicesRef.current?.activate(),
            close: () => servicesRef.current?.close() ?? Promise.resolve(false),
            registerLeaveGuard: (guard, options) =>
              servicesRef.current?.registerLeaveGuard(guard, options) ?? (() => undefined),
          },
    [id, services != null]
  )
  const lifetime = useMemo(() => {
    const retained = new Map<object, () => void>()
    let disposed = false
    let disposalRevision = 0
    const dispose = () => {
      if (disposed) return
      disposed = true
      for (const disposeRetained of [...retained.values()].reverse()) disposeRetained()
      retained.clear()
    }
    return {
      isActive: () => activeRef.current,
      retain(owner: object, dispose: () => void) {
        if (disposed) {
          dispose()
          return
        }
        retained.set(owner, dispose)
      },
      release(owner: object) {
        retained.delete(owner)
      },
      releaseFromEffect(owner: object) {
        if (!disposed && !activeRef.current) return false
        const disposeRetained = retained.get(owner)
        retained.delete(owner)
        disposeRetained?.()
        return true
      },
      connectHost() {
        disposalRevision += 1
      },
      disconnectHost() {
        const revision = ++disposalRevision
        // StrictMode replays effect cleanup/setup synchronously. Delay the
        // irreversible document disposal so the matching setup can cancel it.
        void Promise.resolve().then(() => {
          if (disposalRevision === revision) dispose()
        })
      },
    }
  }, [id])
  useEffect(() => {
    lifetime.connectHost()
    return () => lifetime.disconnectHost()
  }, [lifetime])
  const value = useMemo(
    () => ({ id, active, descriptor, lifetime, services: stableServices }),
    [id, active, descriptor, lifetime, stableServices]
  )
  return (
    <DocumentContext.Provider value={value}>
      <EditScopeProvider key={id} active={active}>
        {children}
      </EditScopeProvider>
    </DocumentContext.Provider>
  )
}

export function useDocumentInstance<TDescriptor = unknown>(): DocumentInstance<TDescriptor> | null {
  const context = useContext(DocumentContext)
  if (context == null) return null
  return {
    id: context.id,
    active: context.active,
    descriptor: context.descriptor as TDescriptor | null,
  }
}

/** Operations for the document instance hosting the current component. */
export function useDocumentInstanceOperations(): DocumentInstanceOperations | null {
  const services = useContext(DocumentContext)?.services
  if (services == null) return null
  return {
    activate: services.activate,
    close: services.close,
  }
}

/** @internal Business lifetimes can outlive Activity-hidden React effects. */
export function useDocumentInstanceLifetime(): DocumentInstanceLifetime | null {
  return useContext(DocumentContext)?.lifetime ?? null
}

/** @internal Framework hooks use the bound instance services for guard registration. */
export function useDocumentInstanceServices(): DocumentInstanceServices | null {
  return useContext(DocumentContext)?.services ?? null
}
